<template>
    <div>
        <v-row class="ma-0">
            <!-- Polling Interval -->
            <v-col cols="2">
                <v-select
                    :items="chartPollingOptions"
                    item-text="label"
                    item-value="value"
                    :label="$t('monitor.home.positionChart.pollingInterval')"
                    v-model="pollingInterval"
                    @change="startPolling"
                />
            </v-col>
            <!-- Window -->
            <v-col cols="2">
                <v-select
                    :items="chartWindowOptions"
                    item-text="label"
                    item-value="value"
                    :label="$t('monitor.home.positionChart.window')"
                    v-model="formData.window"
                />
            </v-col>
            <!-- Last -->
            <v-col cols="2">
                <v-text-field
                    :disabled="isTimeEntered"
                    v-model="formData.last"
                    :label="$t('monitor.home.positionChart.last')"
                    type="number"
                    :rules="[
                        (v) => !!v || 'Last is required.',
                        (v) => v > 0 || 'Last must be greater than 0.',
                    ]"
                    min="1"
                    required
                >
                    <template v-slot:append>
                        <v-menu offset-y dense>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    small
                                    color="default"
                                    dark
                                    :disabled="isTimeEntered"
                                    v-bind="attrs"
                                    v-on="on"
                                    style="height: 19px; margin-top: 2px"
                                >
                                    {{
                                        chartUnitOptions.find(
                                            (option) =>
                                                option.value ===
                                                formData.selectedUnit
                                        ).label
                                    }}
                                </v-btn>
                            </template>
                            <v-list dense>
                                <v-list-item
                                    v-for="item in chartUnitOptions"
                                    :key="item.value"
                                    @click="formData.selectedUnit = item.value"
                                >
                                    <v-list-item-title>{{
                                        item.label
                                    }}</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </template>
                </v-text-field>
            </v-col>
            <!-- Time -->
            <v-col cols="4">
                <v-dialog
                    ref="dialog"
                    v-model="modal"
                    :return-value.sync="formData.dates"
                    persistent
                    width="290px"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                            v-model="dateRangeText"
                            :label="$t('monitor.home.positionChart.time')"
                            append-icon="mdi-calendar"
                            readonly
                            clearable
                            v-bind="attrs"
                            v-on="on"
                            @click:clear="formData.dates = []"
                        ></v-text-field>
                    </template>
                    <v-date-picker
                        v-model="formData.dates"
                        range
                        :allowed-dates="allowedDates"
                    >
                        <v-spacer></v-spacer>
                        <v-btn text color="primary" @click="modal = false">
                            {{ $t("common.form.cancel") }}
                        </v-btn>
                        <v-btn
                            text
                            color="primary"
                            @click="$refs.dialog.save(formData.dates)"
                        >
                            OK
                        </v-btn>
                    </v-date-picker>
                </v-dialog>
            </v-col>
            <v-col cols="2">
                <v-btn
                    @click="loadData"
                    color="primary"
                    block
                    dark
                    small
                    :loading="loading"
                    :disabled="!isFormModified || !isLastValid"
                    style="margin-top: 20px"
                >
                    {{ $t("common.word.fetch") }}
                </v-btn>
            </v-col>
        </v-row>
        <v-row class="ma-0"> </v-row>
        <div id="position-chart" style="width: 1200px; height: 400px"></div>
    </div>
</template>

<script>
import * as echarts from "echarts";
import { getBroker } from "@utils/util";
import { getPositionChart } from "@services/statistic/position-chart";

export default {
    name: "ComponentPositionChart",
    props: {
        book: {
            type: String,
            required: true,
        },
        symbol: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            originalFormData: {
                window: "1m",
                last: "1",
                selectedUnit: "h",
                dates: [],
            },
            formData: {
                window: "1m",
                last: "1",
                selectedUnit: "h",
                dates: [],
            },
            chartPollingOptions: [
                { label: this.$t("common.time.off"), value: "0" },
                { label: this.$t("common.time.5s"), value: "5000" },
                { label: this.$t("common.time.10s"), value: "10000" },
                { label: this.$t("common.time.15s"), value: "15000" },
            ],
            pollingInterval: "0",
            polling: null,
            chartWindowOptions: [
                { label: this.$t("common.time.1min"), value: "1m" },
                { label: this.$t("common.time.5min"), value: "5m" },
                { label: this.$t("common.time.10min"), value: "10m" },
                { label: this.$t("common.time.30min"), value: "30m" },
                { label: this.$t("common.time.1hr"), value: "60m" },
                { label: this.$t("common.time.4hr"), value: "240m" },
            ],
            chartUnitOptions: [
                {
                    label: "Second",
                    value: "s",
                },
                {
                    label: "Minute",
                    value: "m",
                },
                {
                    label: "Hour",
                    value: "h",
                },
            ],
            modal: false,
            broker: getBroker(),
            chart: null,
            option: {
                tooltip: {
                    trigger: "axis",
                    formatter: (params) => {
                        if (!params || !params.length) return "";

                        // Get the time stamp
                        const timestamp = params[0].value[0]; // updated from params to arr
                        // Create Date object
                        const date = new Date(timestamp);

                        // Format time to be consistent with xAxis
                        const timeStr = date.toLocaleString("en-US", {
                            weekday: "short", // ee
                            day: "2-digit", // dd
                            month: "short", // MMM
                            hour: "2-digit", // HH
                            minute: "2-digit", // mm
                            second: "2-digit", // ss
                            hour12: false, // 24-hour format
                        });

                        // Create tooltip content
                        let tooltipContent = `${timeStr}<br/>`;
                        params.forEach((param) => {
                            tooltipContent += `${param.marker} ${
                                param.seriesName
                            }: ${param.value[1].toFixed(2)}<br/>`;
                        });

                        return tooltipContent;
                    },
                },
                grid: {
                    left: "3%",
                    right: "3%",
                    bottom: "5%",
                    top: "5%",
                    containLabel: true,
                },
                xAxis: {
                    type: "time",
                    axisLabel: {
                        formatter: "{dd} {MMM} {HH}:{mm}",
                    },
                },
                yAxis: {
                    type: "value",
                    splitLine: {
                        lineStyle: {
                            color: "#333",
                        },
                    },
                },
                series: [
                    {
                        name: "Position",
                        type: "line",
                        data: [],
                    },
                ],
                darkMode: true,
                color: [
                    "#4992ff",
                    "#7cffb2",
                    "#fddd60",
                    "#ff6e76",
                    "#58d9f9",
                    "#05c091",
                    "#ff8a45",
                    "#8d48e3",
                    "#dd79ff",
                ],
                backgroundColor: "#1e1e1e",
                textStyle: {
                    color: "#B9B8CE",
                },
            },
        };
    },
    computed: {
        dateRangeText() {
            return this.formData.dates.join(" ~ ");
        },
        isTimeEntered() {
            return this.formData.dates.length > 0;
        },
        isFormModified() {
            return (
                JSON.stringify(this.formData) !==
                JSON.stringify(this.originalFormData)
            );
        },
        isLastValid() {
            return this.formData.last && this.formData.last > 0;
        },
    },
    methods: {
        loadData() {
            this.loading = true;
            let params = {
                symbol: JSON.stringify([this.symbol]),
                broker_book: JSON.stringify([this.broker + ":" + this.book]),
                window: this.formData.window,
                last: this.formData.last + this.formData.selectedUnit,
                time: JSON.stringify(
                    this.formData.dates.length > 0
                        ? this.formData.dates.map((date) => date + " 00:00:00")
                        : []
                ),
            };
            getPositionChart(params).then((res) => {
                this.loading = false;
                if (res.data && res.data[0]) {
                    // Ensure the time format is correct
                    const formattedData = res.data[0].x.map((timeStr) => {
                        // If the backend returns string format, convert it to a timestamp
                        const timestamp = new Date(timeStr).getTime();
                        return timestamp;
                    });
                    this.option.series[0].data = formattedData.map(
                        (time, index) => [
                            time,
                            res.data[0].y[this.symbol][index],
                        ]
                    );

                    // Deep clone the formData
                    this.originalFormData = JSON.parse(
                        JSON.stringify(this.formData)
                    );

                    this.chart.setOption(this.option);
                }
            });
        },
        allowedDates(date) {
            const now = new Date();
            now.setHours(23, 59, 59, 999); // Set to the last moment of the day

            const fifteenDaysAgo = new Date();
            fifteenDaysAgo.setDate(fifteenDaysAgo.getDate() - 14);
            fifteenDaysAgo.setHours(0, 0, 0, 0); // Set to the start time 15 days ago

            const currentDate = new Date(date);

            // Only allow selecting dates between 15 days ago and today
            return currentDate >= fifteenDaysAgo && currentDate <= now;
        },
        clearPollingTimer() {
            if (this.polling) {
                clearInterval(this.polling);
                this.polling = null;
            }
        },
        startPolling() {
            this.clearPollingTimer();
            if (this.pollingInterval !== "0") {
                this.loadData();
                this.polling = setInterval(() => {
                    this.loadData();
                }, this.pollingInterval);
            }
        },
    },
    mounted() {
        this.chart = echarts.init(document.getElementById("position-chart"));
        this.loadData();
    },
    beforeDestroy() {
        if (this.chart) {
            this.chart.dispose();
        }
        this.clearPollingTimer();
    },
};
</script>
